import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Backdrop from "@mui/material/Backdrop";
import { GIFT_ENVELOPE_ICON } from "@/constants/campaignBuilder";

export interface GiftMessageModalProps {
  onClose: () => void;
  /** When provided, shows the "Unwrap Gift" CTA (unboxing stage). Omit it on the
   *  landing/collection, where the gift is already unwrapped — message only. */
  onUnwrap?: () => void;
  message?: string;
  isMobile?: boolean;
}

export default function GiftMessageModal({
  onClose,
  onUnwrap,
  message,
  isMobile,
}: GiftMessageModalProps) {
  const cardContent = (
    <>
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        // R02: invisible 44×44 hit area on phones via a ::before expanded 8px past the 28px circle.
        className="flex items-center justify-center transition-colors duration-150 animate-modal-close bg-wax text-graphite hover:bg-smoke cursor-pointer max-md:before:absolute max-md:before:-inset-2"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "none",
          padding: 0,
        }}
        aria-label="Close gift message"
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>

      {/* Gift envelope graphic — matches the redemption flow prototype's modal. */}
      <img
        src={GIFT_ENVELOPE_ICON}
        alt=""
        aria-hidden
        className="animate-modal-icon"
        style={{
          width: 60,
          height: 60,
          objectFit: "contain",
          marginBottom: 18,
          display: "block",
        }}
      />

      {/* Title */}
      <div
        className="animate-modal-title"
        style={{
          fontFamily: "var(--font-crimson)",
          fontSize: 22,
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          marginBottom: 16,
        }}
      >
        Gift Message
      </div>

      {/* Body */}
      <div
        className="animate-modal-body"
        style={{
          fontFamily: "var(--font-crimson)",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 17,
          color: message ? "var(--color-rodeo-black)" : "var(--color-graphite)",
          lineHeight: 1.75,
          textAlign: "left",
          width: "100%",
          minWidth: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
        }}
      >
        {message ||
          "A personalized message from the sender will appear here along with details about the impact your gift creates."}
      </div>

      {/* Unwrap Gift button — only on the unboxing stage (when onUnwrap is given). */}
      {onUnwrap && (
        <button
          type="button"
          onClick={onUnwrap}
          className="inline-flex items-center gap-2 font-lato text-[14px] font-bold tracking-[0.5px] uppercase transition-all duration-150 hover:scale-[1.03] hover:bg-background-dark animate-modal-unwrap"
          style={{
            margin: "36px 0 12px",
            padding: "12px 36px",
            background: "var(--color-rodeo-black)",
            color: "var(--color-white)",
            border: "2px solid var(--color-rodeo-black)",
            borderRadius: 24,
            cursor: "pointer",
          }}
        >
          <CardGiftcardOutlinedIcon sx={{ fontSize: 18 }} />
          Unwrap Gift
        </button>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Backdrop — position:absolute so it fills the fixed inset-0 flow container.
            z above the collection's sticky filter bar (z-50), which is hoisted into the
            same stacking context, so the modal isn't painted behind the type pills. */}
        <Backdrop
          open
          onClick={onClose}
          className="animate-modal-backdrop"
          transitionDuration={0}
          sx={{ position: "absolute", zIndex: 10000, background: "rgba(0,0,0,0.55)" }}
        />

        {/* Card — absolute top/left 50% so gfgCardIn's translate(-50%,-50%) centers it */}
        <div
          className="animate-modal-card"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 10001,
            background: "var(--color-white)",
            borderRadius: 10,
            boxShadow: "0 16px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            padding: "40px 28px 36px",
            width: "calc(100% - 32px)",
            maxHeight: "85%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            transformOrigin: "center center",
            willChange: "transform, opacity",
          }}
        >
          {cardContent}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Full-screen backdrop. z above the builder's FullscreenPreviewOverlay
          (z-9999) so the modal shows when opened from the fullscreen unboxing
          preview; harmless in the inline builder + recipient route (no overlay). */}
      <Backdrop
        open
        onClick={onClose}
        className="animate-modal-backdrop"
        transitionDuration={0}
        sx={{ zIndex: 10000, background: "rgba(0,0,0,0.55)" }}
      />

      {/* Card — centered in viewport */}
      <div
        className="animate-modal-card"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          zIndex: 10001,
          background: "var(--color-white)",
          borderRadius: 10,
          boxShadow: "0 16px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
          padding: "44px 48px 40px",
          width: 440,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "70vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          transformOrigin: "bottom center",
          willChange: "transform, opacity",
        }}
      >
        {cardContent}
      </div>
    </>
  );
}
